import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Alert, CircularProgress } from '@mui/material';
import { PillButton } from '../../superAdminUi';
import ModalShell from './ModalShell';

// A confirm-then-report modal shared by the non-form account actions (Reset Password,
// Reset MFA, Delete User). Each supplies its own copy and the single async `action`;
// this component owns the submitting / success / error states and refreshes the list
// on success. Destructive actions (delete) render the confirm button in error colour.

function ConfirmActionModal({
  open,
  onClose,
  title,
  description,
  confirmLabel,
  successMessage,
  destructive,
  action,
  onCompleted,
}) {
  const [phase, setPhase] = useState('confirm');
  const [error, setError] = useState('');

  const reset = () => {
    setPhase('confirm');
    setError('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleConfirm = async () => {
    setPhase('submitting');
    setError('');
    try {
      await action();
      setPhase('success');
      if (onCompleted) onCompleted();
    } catch (err) {
      setError(err?.message || 'The action could not be completed. Please try again.');
      setPhase('confirm');
    }
  };

  return (
    <ModalShell open={open} onClose={handleClose} title={title}>
      {phase === 'success' ? (
        <Box>
          <Alert severity='success' sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <PillButton type='button' $variant='teal' $size='lg' onClick={handleClose}>
              Done
            </PillButton>
          </Box>
        </Box>
      ) : (
        <Box>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            {description}
          </Typography>
          {error && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <PillButton
              type='button'
              $variant='outline'
              $size='lg'
              onClick={handleClose}
              disabled={phase === 'submitting'}
            >
              Cancel
            </PillButton>
            <PillButton
              type='button'
              $variant={destructive ? 'danger' : 'teal'}
              $size='lg'
              onClick={handleConfirm}
              disabled={phase === 'submitting'}
            >
              {phase === 'submitting' ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : confirmLabel}
            </PillButton>
          </Box>
        </Box>
      )}
    </ModalShell>
  );
}

ConfirmActionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.node.isRequired,
  description: PropTypes.node.isRequired,
  confirmLabel: PropTypes.string.isRequired,
  successMessage: PropTypes.node.isRequired,
  destructive: PropTypes.bool,
  action: PropTypes.func.isRequired,
  onCompleted: PropTypes.func,
};

ConfirmActionModal.defaultProps = {
  destructive: false,
};

export default ConfirmActionModal;
